import React from "react";
import Avatar from "../../../components/ui/Avatar";
import { useNavigate } from "react-router-dom";
import { Radio, WifiOff, Camera, Video } from "lucide-react";

import {
  employeeName,
  formatWorked,
} from "../utils/format";

/**
 * Employees working right now.
 *
 * Polled rather than pushed: the backend exposes no `monitoring:*` socket events,
 * and inventing one here would fork the API contract. `socketConfig.js` is
 * available if the backend later emits them.
 */
const LiveSessionsPanel = ({ records, loading }) => {
  const navigate = useNavigate();

  if (!loading && (!records || records.length === 0)) {
    return (
      <div className="mb-4 bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-6 text-center">
        <p className="text-sm text-gray-500 dark:text-light-500">
          No one is working right now.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-4 bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-100 flex items-center gap-2">
        <Radio className="w-4 h-4 text-green-500" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
          Working now
        </h2>
        <span className="text-xs text-gray-400 dark:text-light-500">
          {records?.length ?? 0}
        </span>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-dark-100">
        {(records ?? []).map((s) => {
          const emp = s.employeeId;
          const isPaused = s.status === "paused";
          return (
            <button
              key={s._id}
              onClick={() => navigate(`/management/monitoring/sessions/${s._id}`)}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors text-left"
            >
              <Avatar src={emp?.photo} name={emp?.fullNameEn || emp?.nickname} size="sm" />

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                  {employeeName(emp)}
                </p>
                <p className="text-xs text-gray-500 dark:text-light-500 flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1 ${
                      isPaused ? "text-amber-500" : "text-green-500"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        isPaused
                          ? "bg-amber-500"
                          : "bg-green-500 animate-pulse-subtle"
                      }`}
                    />
                    {isPaused ? "Paused" : "Active"}
                  </span>

                  {/* Be explicit about what is being captured, not just that a
                      session exists. */}
                  {s.captureMode === "video" || s.captureMode === "both" ? (
                    <span className="inline-flex items-center gap-1">
                      <Video className="w-3 h-3" /> video
                    </span>
                  ) : null}
                  {s.captureMode === "screenshots" ||
                  s.captureMode === "both" ? (
                    <span className="inline-flex items-center gap-1">
                      <Camera className="w-3 h-3" /> {s.screenshotCount ?? 0}
                    </span>
                  ) : null}

                  {/* isStale comes from the server using its own
                      staleSessionMinutes threshold, so the UI never duplicates it. */}
                  {s.isStale && (
                    <span className="inline-flex items-center gap-1 text-red-500">
                      <WifiOff className="w-3 h-3" /> not responding
                    </span>
                  )}
                </p>
              </div>

              <span className="text-sm font-semibold text-gray-900 dark:text-light-100 tabular-nums flex-shrink-0">
                {formatWorked(s.workedSeconds)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LiveSessionsPanel;
